/**
 * usePermissions composable (frontend-rearchitecture, PR-6).
 *
 * Returns `can()` / `cannot()` helpers that test capability strings
 * against the authenticated account's capability list.
 *
 * Capabilities are sourced from the Pinia account store, which is
 * reactive — so `can()` used inside a `computed` or template will
 * re-render automatically when the store hydrates / clears.
 *
 * Example:
 *   const { can, cannot } = usePermissions()
 *   const canEdit = computed(() => can('campaign.edit'))
 *
 * This PR is additive — it does not yet replace existing
 * `localStorage.getItem('roleName')` based gating. That sweep is PR-7+.
 */
import { useAccountStore } from '@/stores/account'

export function usePermissions() {
    const account = useAccountStore()

    /**
     * @param {string} capability  Dotted capability key, e.g. `campaign.edit`.
     * @returns {boolean}          True iff the account has that capability.
     */
    function can(capability) {
        return account.capabilities.includes(capability)
    }

    /**
     * Inverse of `can()`. Useful in templates: `v-if="cannot('order.delete')"`.
     */
    function cannot(capability) {
        return !can(capability)
    }

    return { can, cannot }
}
